import React, { useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { TagBox } from "devextreme-react/tag-box";
import { Button } from "devextreme-react/button";
import data from "../assets/apiRoutes.json";

const CopyDashboard = (props) => {
    const accessToken = props.oidc.user.access_token;
    const webUserInfo = useMemo(() => {
        try {
            return JSON.parse(props.oidc.user.profile.webUserInfo);
        } catch {
            return {};
        }
    }, [props.oidc.user.profile.webUserInfo]);

    const currentUserCompanyId = String(webUserInfo.userCompanyId ?? "");

    const [dashboards, setDashboards] = useState([]);
    const [selectedDashboards, setSelectedDashboards] = useState([]);

    const [facilities, setFacilities] = useState([]);
    const [selectedFacilities, setSelectedFacilities] = useState([]);

    const [loadingDashboards, setLoadingDashboards] = useState(false);
    const [loadingFacilities, setLoadingFacilities] = useState(false);
    const [copying, setCopying] = useState(false);

    useEffect(() => {
        const loadDashboards = async () => {
            setLoadingDashboards(true);
            try {
                const res = await axios.get(
                    `${window._env_.REACT_APP_APIS}/${data.DB.SearchDashboard}`,
                    {
                        params: { dashboardName: "" },
                        headers: {
                            "Content-Type": "application/json",
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );
                const rows = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
                setDashboards(rows);
            } catch (err) {
                console.error("Failed to load dashboards", err);
                notify({ message: "Failed to load dashboards", width: 240 }, "error", 3000);
            } finally {
                setLoadingDashboards(false);
            }
        };

        const loadFacilities = async () => {
            setLoadingFacilities(true);
            try {
                const res = await axios.get(
                    `${window._env_.REACT_APP_AUTH_URL}/${data.idp.GetDatabasesAndCompanies}?userId=${props.oidc.user.profile.sub}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );
                const companies = res.data?.companies ?? [];
                // Exclude the current facility — copying to self is not the use case.
                const targets = companies.filter((c) => String(c.value) !== currentUserCompanyId);
                setFacilities(targets);
            } catch (err) {
                console.error("Failed to load facilities", err);
                notify({ message: "Failed to load facilities", width: 240 }, "error", 3000);
            } finally {
                setLoadingFacilities(false);
            }
        };

        loadDashboards();
        loadFacilities();
    }, [accessToken, currentUserCompanyId, props.oidc.user.profile.sub]);

    const onCopy = async () => {
        if (selectedDashboards.length === 0) {
            notify({ message: "Select at least one dashboard.", width: 280 }, "warning", 2500);
            return;
        }
        if (selectedFacilities.length === 0) {
            notify({ message: "Select at least one target facility.", width: 280 }, "warning", 2500);
            return;
        }

        const targets = facilities
            .filter((f) => selectedFacilities.includes(f.value))
            .map((f) => ({ databaseName: f.text, companyId: f.companyname }));

        setCopying(true);
        try {
            const res = await axios.post(
                `${window._env_.REACT_APP_APIS}/${data.DB.CopyDashboards}`,
                {
                    dashboardRowIds: selectedDashboards,
                    targets,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            const payload = res.data?.resultdata ?? res.data?.Resultdata ?? res.data ?? {};
            const copied = payload.copied ?? 0;
            const skipped = payload.skipped ?? 0;
            const errors = payload.errors ?? [];

            let msg = `Copied ${copied} dashboard(s)`;
            if (skipped > 0) msg += `, skipped ${skipped} (name already exists in target)`;
            if (errors.length > 0) msg += `, ${errors.length} error(s)`;
            notify({ message: msg, width: 380 }, errors.length > 0 ? "warning" : "success", 4000);

            if (errors.length > 0) {
                console.warn("Copy errors:", errors);
            }
        } catch (err) {
            console.error("Copy failed", err);
            notify({ message: "Copy failed. See console for details.", width: 320 }, "error", 4000);
        } finally {
            setCopying(false);
        }
    };

    return (
        <div style={{ padding: 20, maxWidth: 720 }}>
            <h6 style={{ color: window._env_.Color2, fontWeight: 700, marginBottom: 20 }}>
                Copy Dashboard
            </h6>

            <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                    Dashboards to copy (from current facility)
                </label>
                <TagBox
                    dataSource={dashboards}
                    valueExpr="rowid"
                    displayExpr="layoutname"
                    value={selectedDashboards}
                    onValueChanged={(e) => setSelectedDashboards(e.value || [])}
                    searchEnabled={true}
                    showSelectionControls={true}
                    placeholder={loadingDashboards ? "Loading..." : "Select dashboards"}
                    multiline={true}
                    stylingMode="outlined"
                />
            </div>

            <div style={{ marginBottom: 24 }}>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                    Target facilities
                </label>
                <TagBox
                    dataSource={facilities}
                    valueExpr="value"
                    displayExpr={(item) =>
                        item ? `${item.companyname || ""} — ${item.text || ""}` : ""
                    }
                    value={selectedFacilities}
                    onValueChanged={(e) => setSelectedFacilities(e.value || [])}
                    searchEnabled={true}
                    showSelectionControls={true}
                    placeholder={loadingFacilities ? "Loading..." : "Select target facilities"}
                    multiline={true}
                    stylingMode="outlined"
                />
            </div>

            <Button
                text={copying ? "Copying..." : "Copy"}
                type="default"
                disabled={
                    copying ||
                    loadingDashboards ||
                    loadingFacilities ||
                    selectedDashboards.length === 0 ||
                    selectedFacilities.length === 0
                }
                onClick={onCopy}
            />

            <p style={{ marginTop: 24, fontSize: 12, color: "#666" }}>
                Dashboards with a name that already exists in a target facility are skipped.
            </p>
        </div>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(CopyDashboard);
